<template>
  <div
    class="dropdown_container"
    :class="[{ dropdown_disabled: disabled }, width || 'w-fit']"
  >
    <div
      :class="{ hover_area: hoverAreaNeeded }"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
      <div
        v-if="show"
        ref="triggerRef"
        :class="['trigger_container', width]"
        @click="handleTrigger"
      >
        <slot name="trigger"></slot>
      </div>
      <div
        v-if="isOpen"
        ref="dropdownRef"
        :class="[
          'dropdown custom_scrollbar',
          {
            fade_out: isClosing,
            center: adjustedPosition === 'center',
            top: adjustedPosition === 'top' || props.position === 'top',
            bottom: adjustedPosition === 'bottom',
            topstart:
              adjustedPosition === 'topstart' || props.position === 'topstart' || (useFixed && isFlippedUp),
            topend:
              adjustedPosition === 'topend' || props.position === 'topend',
            left: adjustedPosition === 'left' || props.position === 'left',
            right: adjustedPosition === 'right' || props.position === 'right',
          },
          dropdownCSS,
        ]"
        :style="{
          ...dropdownStyle,
          '--dropdown-position': useFixed ? 'fixed' : 'absolute',
        }"
        @mouseenter="clearCloseTimeout"
        @mouseleave="handleMouseLeave"
      >
        <slot></slot>
      </div>
    </div>

    <div
      v-if="showSubsection"
      ref="subsectionDropdownRef"
      class="subsection_dropdown custom_scrollbar"
      :class="{ 'fade-out': isClosingSubsection }"
      :style="subsectionStyle"
      @mouseenter="clearCloseSubsectionTimeout"
      @mouseleave="startCloseSubsection"
    >
      <slot name="subsection"></slot>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  onMounted,
  onBeforeUnmount,
  computed,
  watch,
  nextTick,
} from "vue";

const props = defineProps({
  onHover: { type: Boolean, default: false },
  maxHeight: { type: String, default: null },
  gap: { type: String, default: null },
  position: {
    type: String,
    default: "start",
    validator: (value) =>
      [
        "start",
        "end",
        "center",
        "top",
        "topstart",
        "topend",
        "left",
        "right",
      ].includes(value),
  },
  show: { type: Boolean, default: true },
  dropdownCSS: { type: String, default: "p-2" },
  relativeToViewport: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  overflowY: {
    type: String,
    default: "scroll",
    validator: (value) =>
      ["scroll", "auto", "hidden", "visible"].includes(value),
  },
  width: { type: String, default: "" },
  hoverDelay: { type: Number, default: 1000 },
  hoverAreaNeeded: { type: Boolean, default: true },
  subsectionDirection: {
    type: String,
    default: "right",
    validator: (value) => ["left", "right"].includes(value),
  },
  hasSubsection: { type: Boolean, default: false },
  subsectionTriggerSelector: { type: String, default: null },
});

const emit = defineEmits([
  "close",
  "triggerClick",
  "subsectionOpen",
  "subsectionClose",
]);

const isOpen = ref(false);
const isClosing = ref(false);
const dropdownRef = ref(null);
const closeTimeoutId = ref(null);
const resizeObserver = ref(null);
const triggerRef = ref(null);
const adjustedPosition = ref(props.position);
const subsectionDropdownRef = ref(null);
const showSubsection = ref(false);
const isClosingSubsection = ref(false);
const subsectionCloseTimeoutId = ref(null);
const subsectionStyle = ref({});
const subsectionTriggerElement = ref(null);
const useFixed = ref(false);
const isFlippedUp = ref(false);

const checkIfNeedsFixed = () => {
  if (props.relativeToViewport) {
    useFixed.value = true;
    return;
  }
  // Walk ancestors to find any element with overflow-y: auto/scroll.
  // Those containers clip position:absolute children, so we need position:fixed
  // to escape them. Stops at document.body so it doesn't traverse the whole page.
  let el = triggerRef.value?.parentElement;
  while (el && el !== document.body) {
    const oy = window.getComputedStyle(el).overflowY;
    if (oy === "auto" || oy === "scroll") {
      useFixed.value = true;
      return;
    }
    el = el.parentElement;
  }
  useFixed.value = false;
};

const handleClickOutside = (event) => {
  const isDatePickerClick =
    event.target.closest(".ant-picker-dropdown") ||
    event.target.closest(".ant-picker") ||
    event.target.closest("#custom_date_filter");

  const isAntDropdownClick =
    event.target.closest(".ant-dropdown") ||
    event.target.closest(".ant-dropdown-menu") || 
    event.target.closest(".ant-select-dropdown");

  const clickedInSubsection =
    subsectionDropdownRef.value &&
    subsectionDropdownRef.value.contains(event.target);

  if (
    dropdownRef.value &&
    !dropdownRef.value.contains(event.target) &&
    !isDatePickerClick &&
    !isAntDropdownClick &&
    !clickedInSubsection
  ) {
    closeDropdown();
  }
};

const clearCloseTimeout = () => {
  if (closeTimeoutId.value) {
    clearTimeout(closeTimeoutId.value);
    closeTimeoutId.value = null;
  }
};

const clearCloseSubsectionTimeout = () => {
  if (subsectionCloseTimeoutId.value) {
    clearTimeout(subsectionCloseTimeoutId.value);
    subsectionCloseTimeoutId.value = null;
  }
};

const startCloseSubsection = () => {
  clearCloseSubsectionTimeout();
  subsectionCloseTimeoutId.value = setTimeout(() => {
    isClosingSubsection.value = true;
    setTimeout(() => {
      showSubsection.value = false;
      isClosingSubsection.value = false;
      emit("subsectionClose");
    }, 150);
  }, 300);
};

const handleTrigger = () => {
  if (props.disabled) {
    return;
  }
  emit("triggerClick", {
    isOpen: isOpen.value,
    event: "click",
  });
  if (isOpen.value) {
    closeDropdown();
  } else {
    checkIfNeedsFixed();
    isOpen.value = true;
    nextTick(() => {
      updateDropdownPosition();
      setupSubsectionTrigger();
    });
  }
};

const setupSubsectionTrigger = () => {
  if (!props.hasSubsection || !props.subsectionTriggerSelector) return;

  nextTick(() => {
    const trigger = dropdownRef.value?.querySelector(
      props.subsectionTriggerSelector,
    );

    if (trigger) {
      subsectionTriggerElement.value = trigger;

      trigger.addEventListener("mouseenter", openSubsection);
      trigger.addEventListener("mouseleave", startCloseSubsection);
    }
  });
};

const openSubsection = () => {
  clearCloseSubsectionTimeout();
  showSubsection.value = true;
  emit("subsectionOpen");

  nextTick(() => {
    updateSubsectionPosition();
  });
};

const updateSubsectionPosition = () => {
  if (!subsectionTriggerElement.value || !subsectionDropdownRef.value) return;

  const triggerRect = subsectionTriggerElement.value.getBoundingClientRect();
  const dropdownRect = subsectionDropdownRef.value.getBoundingClientRect();
  const viewportWidth = window.innerWidth;

  if (props.subsectionDirection === "right") {
    subsectionStyle.value = {
      position: "fixed",
      top: `${triggerRect.top}px`,
      left: `${triggerRect.right + 8}px`,
      zIndex: "100",
    };

    if (triggerRect.right + 8 + dropdownRect.width > viewportWidth) {
      subsectionStyle.value.left = `${triggerRect.left - dropdownRect.width - 8}px`;
    }
  } else {
    subsectionStyle.value = {
      position: "fixed",
      top: `${triggerRect.top}px`,
      left: `${triggerRect.left - dropdownRect.width - 8}px`,
      zIndex: "100",
    };

    if (triggerRect.left - dropdownRect.width - 8 < 0) {
      subsectionStyle.value.left = `${triggerRect.right + 8}px`;
    }
  }

  const viewportHeight = window.innerHeight;
  if (
    parseInt(subsectionStyle.value.top) + dropdownRect.height >
    viewportHeight
  ) {
    subsectionStyle.value.top = `${viewportHeight - dropdownRect.height - 8}px`;
  }
};

const handleMouseEnter = () => {
  if (props.onHover && !props.disabled) {
    clearCloseTimeout();
    checkIfNeedsFixed();
    isOpen.value = true;
  }
};

const handleMouseLeave = () => {
  if (props.onHover) {
    clearCloseTimeout();
    closeTimeoutId.value = setTimeout(() => {
      closeDropdown();
    }, props.hoverDelay);
  }
};

const closeDropdown = () => {
  isClosing.value = true;

  if (showSubsection.value) {
    isClosingSubsection.value = true;
  }

  setTimeout(() => {
    isOpen.value = false;
    isClosing.value = false;
    showSubsection.value = false;
    isClosingSubsection.value = false;
    useFixed.value = false;
    isFlippedUp.value = false;
    emit("close");
  }, 100);
};

const dropdownPosition = ref({});

// When a CSS transform exists on any ancestor, position:fixed children are
// positioned relative to that ancestor instead of the viewport. Find the nearest
// such ancestor and return its viewport offset so we can correct our coordinates.
const getTransformAncestorOffset = (el) => {
  let ancestor = el.parentElement;
  while (ancestor && ancestor !== document.body) {
    if (window.getComputedStyle(ancestor).transform !== "none") {
      const r = ancestor.getBoundingClientRect();
      return { top: r.top, left: r.left };
    }
    ancestor = ancestor.parentElement;
  }
  return { top: 0, left: 0 };
};

const updateDropdownPosition = () => {
  if (useFixed.value) {
    const triggerEl = triggerRef.value;
    const dropdown = dropdownRef.value;
    if (!triggerEl || !dropdown) return;

    const triggerRect = triggerEl.getBoundingClientRect();
    const dropdownHeight = dropdown.offsetHeight;
    const dropdownWidth = dropdown.offsetWidth;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const gapPx = props.gap ? parseInt(props.gap) : 0;

    // Correct for any CSS-transform ancestor that acts as the fixed-position
    // containing block (modal open animations leave transform:scale(1) on the
    // modal element, which offsets position:fixed children by the modal's own
    // viewport position).
    const offset = getTransformAncestorOffset(triggerEl);

    // Horizontal: align based on position prop, in viewport coords
    let left;
    if (props.position === "end" || props.position === "topend") {
      left = triggerRect.right - dropdownWidth;
    } else if (props.position === "center") {
      left = triggerRect.left + triggerRect.width / 2 - dropdownWidth / 2;
    } else {
      left = triggerRect.left;
    }
    if (left + dropdownWidth > viewportWidth) left = viewportWidth - dropdownWidth - 4;
    if (left < 0) left = 4;

    // Prefer below; flip above only when it genuinely can't fit below
    const spaceBelow = viewportHeight - triggerRect.bottom - gapPx;
    const spaceAbove = triggerRect.top - gapPx;
    let top;

    if (dropdownHeight <= spaceBelow || spaceBelow >= spaceAbove) {
      top = triggerRect.bottom + gapPx;
      isFlippedUp.value = false;
    } else {
      top = triggerRect.top - gapPx - dropdownHeight;
      isFlippedUp.value = true;
    }

    // Subtract the transform ancestor's viewport offset to get coordinates
    // relative to the fixed-position containing block
    dropdownPosition.value = {
      top: `${top - offset.top}px`,
      left: `${left - offset.left}px`,
    };
    return;
  }

  dropdownPosition.value = {
    start: { left: "0" },
    end: { right: "0", left: "auto" },
    center: { left: "50%", transform: "translateX(-50%)" },
    top: { bottom: "100%", left: "0" },
    topstart: { bottom: "100%", left: "0" },
    topend: { bottom: "100%", right: "0" },
    left: { right: "100%", top: "0" },
    right: { left: "100%", top: "0" },
  }[props.position];
};

const dropdownStyle = computed(() => ({
  maxHeight: props.maxHeight,
  overflowY: props.overflowY,
  ...(useFixed.value
    ? { zIndex: "99" }
    : props.position.startsWith("top")
      ? { marginBottom: props.gap }
      : props.position === "left" || props.position === "right"
        ? { margin: "0 " + props.gap }
        : { marginTop: props.gap }),
  ...dropdownPosition.value,
}));

const setupEventHandlers = () => {
  if (props.hasSubsection && props.subsectionTriggerSelector) {
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("resize", handleResize);

    nextTick(() => {
      updateDropdownPosition();
      setupSubsectionTrigger();
    });
  } else {
    document.addEventListener("mousedown", handleClickOutside);

    nextTick(() => {
      updateDropdownPosition();
    });
  }
};

const setupResizeObserver = () => {
  if (dropdownRef.value && ResizeObserver) {
    resizeObserver.value = new ResizeObserver(() => {
      if (isOpen.value) {
        updateDropdownPosition();
      }
    });
    resizeObserver.value.observe(dropdownRef.value);
  }
};

const handleResize = () => {
  if (isOpen.value) {
    updateDropdownPosition();
  }

  if (showSubsection.value) {
    updateSubsectionPosition();
  }
};

const cleanupEventHandlers = () => {
  document.removeEventListener("mousedown", handleClickOutside);
  window.removeEventListener("resize", handleResize);

  if (subsectionTriggerElement.value) {
    subsectionTriggerElement.value.removeEventListener(
      "mouseenter",
      openSubsection,
    );
    subsectionTriggerElement.value.removeEventListener(
      "mouseleave",
      startCloseSubsection,
    );
  }

  clearCloseTimeout();
  clearCloseSubsectionTimeout();
  if (resizeObserver.value) {
    resizeObserver.value.disconnect();
    resizeObserver.value = null;
  }
};

const adjustDropdownPosition = () => {
  if (useFixed.value) return;

  const dropdown = dropdownRef.value;
  if (!dropdown) return;

  const screenHeight = window.innerHeight;
  const dropdownHeight = dropdown.offsetHeight;
  const dropdownY = dropdown.getBoundingClientRect().top;

  if (dropdownY + dropdownHeight > screenHeight) {
    adjustedPosition.value = "bottom";
  } else {
    adjustedPosition.value = "top";
  }
};

onMounted(setupEventHandlers);
onBeforeUnmount(cleanupEventHandlers);

watch(isOpen, (newValue) => {
  if (newValue) {
    nextTick(() => {
      updateDropdownPosition();
      setupSubsectionTrigger();
      setupResizeObserver();
      adjustDropdownPosition();
    });
  } else {
    showSubsection.value = false;
    if (resizeObserver.value) {
      resizeObserver.value.disconnect();
      resizeObserver.value = null;
    }
  }
});

defineExpose({
  toggleDropdown: handleTrigger,
  closeDropdown: () => {
    isOpen.value = false;
  },
  isOpen,
  showSubsection,
  openSubsection,
  closeSubsection: () => {
    showSubsection.value = false;
  },
});
</script>

<style scoped>
.dropdown_container {
  position: relative;
  display: inline-block;
  height: fit-content;
}

.hover_area {
  padding: 8px;
  margin: -8px;
}

.trigger_container {
  display: inline-block;
}

.dropdown {
  position: var(--dropdown-position, absolute);
  color: #fff;
  border-radius: 8px;
  background: var(--dropdown-bg);
  box-shadow: var(--dropdown-boxshadow);
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
  opacity: 0;
  animation: fade_in 0.3s forwards;
  width: max-content;
  z-index: 50;
}

.subsection_dropdown {
  position: fixed;
  border-radius: 8px;
  background: var(--dropdown-bg);
  box-shadow: var(--dropdown-boxshadow);
  padding: 8px;
  z-index: 1000;
  min-width: 140px;
  animation: fade-in 0.15s ease;
}

.fade-out {
  animation: fade-out 0.15s ease forwards;
}

.dropdown.center {
  left: 50%;
  transform: translateX(-50%);
  animation: fade_in_center 0.3s forwards;
}

.dropdown.top,
.dropdown.topstart,
.dropdown.topend {
  animation: fade_in_top 0.3s forwards;
}

.dropdown.left {
  animation: fade_in_left 0.3s forwards;
}

.dropdown.right {
  animation: fade_in_right 0.3s forwards;
}

.dropdown.center.fade_out {
  animation: fade_out_center 0.3s forwards;
}

.dropdown.top.fade_out,
.dropdown.topstart.fade_out,
.dropdown.topend.fade_out {
  animation: fade_out_top 0.3s forwards;
}

.dropdown.left.fade_out {
  animation: fade_out_left 0.3s forwards;
}

.dropdown.right.fade_out {
  animation: fade_out_right 0.3s forwards;
}

@keyframes fade_in {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fade_out {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-10px);
  }
}

@keyframes fade_in_center {
  from {
    opacity: 0;
    transform: translateY(-10px) translateX(-50%);
  }
  to {
    opacity: 1;
    transform: translateY(0) translateX(-50%);
  }
}

@keyframes fade_out_center {
  from {
    opacity: 1;
    transform: translateY(0) translateX(-50%);
  }
  to {
    opacity: 0;
    transform: translateY(-10px) translateX(-50%);
  }
}

@keyframes fade_in_top {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fade_out_top {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(10px);
  }
}

@keyframes fade_in_left {
  from {
    opacity: 0;
    transform: translateX(10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fade_out_left {
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(10px);
  }
}

@keyframes fade_in_right {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fade_out_right {
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(-10px);
  }
}

@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateX(-5px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fade-out {
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(-5px);
  }
}

.fade_out:not(.center):not(.top):not(.topstart):not(.topend):not(.left):not(
    .right
  ) {
  animation: fade_out 0.3s forwards;
}

.dropdown_disabled {
  opacity: 0.5;
  cursor: not-allowed !important;
}

.dropdown_disabled * {
  pointer-events: none;
}
</style>
